import { DownOutlined } from "@ant-design/icons"
import { Alert, Button, Descriptions, Dropdown, Menu, Modal } from "antd"
import { DateDisplay } from "components/display/DateDisplay"
import { MoneyDisplay } from "components/display/MoneyDisplay"
import {
  IMTAInformation,
  IPricingRateableComponentPrice,
  IPricingServiceFee
} from "contexts/opus/context"
import { IOrgSettings } from "contexts/organisation/context"
import { useOrgSettings } from "contexts/organisation/hooks/use-org-settings"
import {
  ICompanyInRole,
  IPriceSectionHeader,
  PaymentPlanType
} from "platform-client/types"
import { IQuoteChange, Proposer } from "platform-client/types/quote"
import React from "react"
import styled from "styled-components"
import { IV2PaymentPlan } from "../../index.v2"
import { RoadsideAssistanceMappings } from "utils/data/stella-roadside-mapping"

const StyledMoneyDescriptions = styled(Descriptions)`
  .ant-descriptions-item.total .ant-descriptions-item-label,
  .ant-descriptions-item.total .ant-descriptions-item-content {
    font-weight: bold;
  }

  .ant-descriptions-item-content {
    justify-content: flex-end;
  }
`

const friendlyPaymentPlanName: Record<string, string> = {
  PayOnAccount: "Pay by invoice",
  FixedTermSinglePayment: "Pay by card",
  ExternalCollection: "Pay on account"
}

const QuoteBindWithConfirmation: React.FC<IQuoteBindConfirmationProps> = ({
  onClose,
  onBindQuote,
  proposer,
  quoteReferenceID,
  quoteChanges = [],
  quoteCompanies = [],
  quotePaymentPlans,
  mtaInfo,
  coverStartDate,
  coverEndDate,
  mtaEffectiveDate,
  blockBind
}) => {
  const { orgSettings } = useOrgSettings()
  const [visible, setVisible] = React.useState<boolean>(false)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [selectedPaymentPlan, setSelectedPaymentPlan] =
    React.useState<DisplayPaymentPlan>()
  const personName = proposer && "personName" in proposer && proposer.personName
  const companyName =
    proposer && "companyName" in proposer && proposer.companyName
  const introducer = [...quoteCompanies].shift()

  const hasChanges = quoteChanges.length > 0
  const updatedCoverStartDate = quoteChanges.find(
    (item) => item.propertyIdentifier === "CoverStartDate"
  )
  const updatedCoverEndDate = quoteChanges.find(
    (item) => item.propertyIdentifier === "CoverEndDate"
  )

  const paymentPlans: DisplayPaymentPlan[] = (quotePaymentPlans ?? []).map(
    (plan) => ({
      pricingPlanReferenceID: plan.price.pricingPlanReferenceID,
      planType: plan.price.planType,
      fees: plan.fees,
      rateableComponentFinalPremium: plan.rateableComponentFinalPremium,
      finalPremium: plan.price.final,
      totalPayable: plan.price.total
    })
  )

  if (mtaInfo) {
    const mtaPrice = {
      gross: mtaInfo.totalPriceChangeBreakdown.gross,
      net: mtaInfo.totalPriceChangeBreakdown.net,
      introducerCommission: mtaInfo.totalPriceChangeBreakdown.brokerCommission,
      tax: {
        total: mtaInfo.totalPriceChangeBreakdown.tax.total,
        taxes: [],
        taxRuleID: mtaInfo.totalPriceChangeBreakdown.tax.taxRuleID,
        taxRuleReferenceID:
          mtaInfo.totalPriceChangeBreakdown.tax.taxPlanReferenceID
      }
    }

    const mtaDisplayPlan: DisplayPaymentPlan = {
      pricingPlanReferenceID: mtaInfo.paymentPlan.paymentPlanReferenceID,
      planType: mtaInfo.paymentPlan.type,
      fees: undefined,
      rateableComponentFinalPremium: [],
      finalPremium: mtaPrice,
      totalPayable: mtaPrice
    }
    paymentPlans.push(mtaDisplayPlan)
  }

  const handleBind = () => {
    setLoading(true)
    if (selectedPaymentPlan) {
      if (typeof onBindQuote === "function") {
        let pp: IV2PaymentPlan | undefined = undefined

        if (!mtaInfo && quotePaymentPlans && quotePaymentPlans.length > 0) {
          pp = quotePaymentPlans.find(
            (p) =>
              p.price.pricingPlanReferenceID ===
              selectedPaymentPlan.pricingPlanReferenceID
          )
        }

        onBindQuote(pp, () => {
          setVisible(false)
          setLoading(false)
        })
      }
    }
  }

  const handleCancel = () => {
    setVisible(false)
    if (typeof onClose === "function") {
      onClose()
    }
  }

  return (
    <>
      {paymentPlans && paymentPlans.length > 1 ? (
        <Dropdown
          disabled={blockBind === true}
          overlay={
            <Menu onClick={() => setVisible(!visible)}>
              {paymentPlans &&
                paymentPlans.map((item) => (
                  <Menu.Item
                    key={item.pricingPlanReferenceID}
                    aria-label={item.planType}
                    onClick={() => {
                      setVisible(true)
                      setSelectedPaymentPlan(item)
                    }}
                  >
                    {friendlyPaymentPlanName[item.planType]}
                  </Menu.Item>
                ))}
            </Menu>
          }
        >
          <Button
            loading={loading}
            aria-label="bind-quote"
            type="primary"
            disabled={blockBind === true}
          >
            Bind <DownOutlined />
          </Button>
        </Dropdown>
      ) : paymentPlans && paymentPlans.length === 1 ? (
        <Button
          loading={loading}
          aria-label="bind-quote"
          type="primary"
          disabled={blockBind === true}
          onClick={() => {
            setSelectedPaymentPlan(paymentPlans[0])
            setVisible(true)
          }}
        >
          Bind
        </Button>
      ) : null}
      <Modal
        visible={visible}
        footer={[
          <Button
            loading={loading}
            type="primary"
            onClick={handleBind}
            disabled={blockBind === true}
          >
            Bind
          </Button>,
          <Button type="default" onClick={handleCancel}>
            Close
          </Button>
        ]}
        onCancel={handleCancel}
      >
        <h1>Bind Quote</h1>

        {hasChanges && (
          <Alert
            style={{ marginBottom: "14px" }}
            message="The following changes have been made to this quote"
            type="warning"
            description={
              <ol style={{ margin: 0, paddingLeft: "14px" }}>
                {quoteChanges.map((change) => (
                  <li>{change.message}</li>
                ))}
              </ol>
            }
          />
        )}

        <hr />

        <Descriptions size="small" column={1} style={{ margin: "20px 0 0" }}>
          <Descriptions.Item label="Quote Reference">
            {quoteReferenceID}
          </Descriptions.Item>
          {coverStartDate && (
            <Descriptions.Item label="Cover Start Date">
              <DateDisplay
                date={
                  updatedCoverStartDate
                    ? String(updatedCoverStartDate.newValue)
                    : coverStartDate
                }
              />
            </Descriptions.Item>
          )}
          {coverEndDate && (
            <Descriptions.Item label="Cover End Date">
              <DateDisplay
                date={
                  updatedCoverEndDate
                    ? String(updatedCoverEndDate.newValue)
                    : coverEndDate
                }
              />
            </Descriptions.Item>
          )}
          {mtaEffectiveDate && (
            <Descriptions.Item label="MTA Effective Date">
              <DateDisplay date={mtaEffectiveDate} />
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Client">
            {personName && `${personName.firstName} ${personName.lastName}`}
          </Descriptions.Item>
          {companyName && (
            <Descriptions.Item label="The Insured - Named Insured">
              {companyName}
            </Descriptions.Item>
          )}
          {introducer && introducer.company && (
            <Descriptions.Item label="Sub-broker/Introducer">
              {introducer.company.name}
            </Descriptions.Item>
          )}
        </Descriptions>
        <hr />
        {selectedPaymentPlan && orgSettings && (
          <TotalsDisplay
            fees={selectedPaymentPlan.fees}
            orgSettings={orgSettings}
            rateableComponentFinalPremium={
              selectedPaymentPlan.rateableComponentFinalPremium
            }
            finalPremium={selectedPaymentPlan.finalPremium}
            totalPayable={selectedPaymentPlan.totalPayable}
          />
        )}
      </Modal>
    </>
  )
}

const TotalsDisplay: React.FC<ITotalsDisplayProps> = ({
  fees,
  rateableComponentFinalPremium,
  finalPremium,
  totalPayable,
  orgSettings
}) => {
  const totalIntroducerCommission =
    finalPremium && "introducerCommission" in finalPremium
      ? finalPremium.introducerCommission
      : undefined

  let totalFees: number

  if (fees) {
    totalFees = fees.reduce((a, v) => a + v.amount.value, 0)
  } else {
    totalFees = finalPremium.rows
      ? finalPremium.rows?.reduce(
          (a, c) => (c.type === "Fee" ? a + c.gross.value : a),
          0
        )
      : 0
  }

  let insurancePremium = finalPremium.net
  if (insurancePremium && totalIntroducerCommission) {
    insurancePremium = {
      value: insurancePremium.value - totalIntroducerCommission.value,
      currencyCode: orgSettings?.currencyCode ?? "AUD"
    }
  }

  return (
    <StyledMoneyDescriptions
      size="small"
      column={1}
      style={{ margin: "20px 0 0" }}
    >
      {finalPremium.net && (
        <Descriptions.Item label="Insurance Premium">
          <MoneyDisplay value={insurancePremium} />
        </Descriptions.Item>
      )}
      {totalIntroducerCommission && (
        <Descriptions.Item label="Broker Commission">
          <MoneyDisplay value={totalIntroducerCommission} />
        </Descriptions.Item>
      )}
      {finalPremium.tax?.total && (
        <Descriptions.Item label="Total Tax">
          <MoneyDisplay value={finalPremium.tax.total} />
        </Descriptions.Item>
      )}
      {rateableComponentFinalPremium
        ?.filter((re) => re.isStandaloneProduct === true)
        ?.map((re) => {
          const rcLabel =
            RoadsideAssistanceMappings[re.referenceID] || re.referenceID

          return (
            <Descriptions.Item key={re.referenceID} label={rcLabel}>
              <MoneyDisplay value={re.price.gross} />
            </Descriptions.Item>
          )
        })}

      {totalFees && (
        <Descriptions.Item label="Underwriting Fee">
          <MoneyDisplay
            value={{
              value: totalFees,
              currencyCode: orgSettings?.currencyCode ?? "GBP"
            }}
          />
        </Descriptions.Item>
      )}

      {totalPayable.gross && (
        <Descriptions.Item className="total" label="Amount Owed">
          <MoneyDisplay value={totalPayable.gross} />
        </Descriptions.Item>
      )}
    </StyledMoneyDescriptions>
  )
}

export default QuoteBindWithConfirmation

interface ITotalsDisplayProps {
  fees?: IPricingServiceFee[]
  rateableComponentFinalPremium?: IPricingRateableComponentPrice[]
  finalPremium: IPriceSectionHeader
  totalPayable: IPriceSectionHeader
  orgSettings: IOrgSettings
}

type DisplayPaymentPlan = {
  pricingPlanReferenceID: string
  planType: PaymentPlanType
  fees?: IPricingServiceFee[]
  rateableComponentFinalPremium?: IPricingRateableComponentPrice[]
  finalPremium: IPriceSectionHeader
  totalPayable: IPriceSectionHeader
}

interface IQuoteBindConfirmationProps {
  quoteReferenceID: string
  proposer?: Proposer
  quoteCompanies?: ICompanyInRole[]
  quotePaymentPlans?: IV2PaymentPlan[]
  mtaInfo?: IMTAInformation
  onBindQuote?: (
    paymentPlan: IV2PaymentPlan | undefined,
    callback?: () => void
  ) => void
  onClose?: () => void
  blockBind?: boolean
  quoteChanges?: IQuoteChange[]
  coverStartDate?: string
  coverEndDate?: string
  mtaEffectiveDate?: string
}
